'use client'

import { useEffect, type ReactNode } from 'react'
import { ExternalLink } from 'lucide-react'
import { ErrorState } from '@/components/error-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useOperations } from '@/hooks/useOperations'
import type { OperationsReport, SectionStatus } from '@/lib/api/operations'

type Problem = string

/**
 * Everything on the report that needs a person. A section that could not be read is a problem too:
 * green is shown only for what was actually checked.
 */
function problems(report: OperationsReport): Problem[] {
    const found: Problem[] = []
    const unread = (label: string, status: string) => { if (status === 'unknown') { found.push(`${label} could not be read`) } }
    unread('Alarms', report.alarms.status)
    unread('Dead-letter queues', report.deadLetters.status)
    unread('The uptime check', report.uptime.status)
    unread('The budget', report.budget.status)
    unread('Deploys', report.deploys.status)
    for (const alarm of report.alarms.items ?? []) { if (alarm?.state === 'ALARM') { found.push(`${alarm.name} is in alarm`) } }
    for (const queue of report.deadLetters.items ?? []) { if (queue && queue.messages > 0) { found.push(`${queue.name} holds ${queue.messages} message${queue.messages === 1 ? '' : 's'}`) } }
    if (report.uptime.status === 'ok' && report.uptime.lastRunState && report.uptime.lastRunState !== 'PASSED') { found.push(`The last uptime check ${report.uptime.lastRunState.toLowerCase()}`) }
    for (const app of ['Site', 'Admin']) {
        const latest = (report.deploys.items ?? []).find((deploy) => deploy?.app === app)
        if (latest && ['FAILED', 'CANCELLED'].includes(latest.status)) { found.push(`The ${app.toLowerCase()}'s latest deploy ${latest.status.toLowerCase()}`) }
    }
    return found
}

export function OperationsView() {
    const { data: report, isPending, isError, refetch, isFetching } = useOperations()
    const open = report ? problems(report) : []

    // The tab says how many things need a person, so a pinned tab is worth glancing at.
    useEffect(() => {
        document.title = open.length > 0 ? `(${open.length}) Operations · Admin` : 'Operations · Admin'
    }, [open.length])

    if (isPending) {
        return (
            <div className="grid gap-6 lg:grid-cols-2">
                {Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-48 rounded-xl" />)}
            </div>
        )
    }
    if (isError || !report) {
        return <ErrorState title="Could not read this environment's health" body="The operations query failed — the Sentry issue tagged surface: admin says why." onRetry={() => void refetch()} />
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div role="status" aria-live="polite">
                    {open.length === 0
                        ? <p className="font-medium text-success">Nothing needs attention</p>
                        : (
                            <>
                                <p className="font-medium text-destructive">{open.length} thing{open.length === 1 ? '' : 's'} need attention</p>
                                <ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">{open.map((problem) => <li key={problem}>{problem}</li>)}</ul>
                            </>
                        )}
                    <p className="mt-1 text-xs text-muted-foreground">{report.environment} · checked {new Date(report.checkedAt).toLocaleTimeString()}</p>
                </div>
                <Button variant="outline" size="sm" pending={isFetching} onClick={() => void refetch()}>{isFetching ? 'Checking…' : 'Check again'}</Button>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                <Section title="Alarms" description={`Every alarm named ${report.environment}-…. Prove they reach you with make alarm-drill, which fires ${report.drillAlarm}.`} status={report.alarms.status} detail={report.alarms.detail} absent="No alarms exist in this environment yet.">
                    <ul className="divide-y divide-border">
                        {(report.alarms.items ?? []).map((alarm) => alarm && (
                            <li key={alarm.name} className="flex items-start justify-between gap-3 py-2">
                                <div className="min-w-0">
                                    <ConsoleLink href={alarm.consoleUrl}>{alarm.name}</ConsoleLink>
                                    {alarm.state === 'ALARM' && alarm.reason && <p className="mt-0.5 text-xs text-muted-foreground">{alarm.reason}</p>}
                                </div>
                                <Badge variant={alarm.state === 'ALARM' ? 'destructive' : alarm.state === 'OK' ? 'success' : 'secondary'}>{alarm.state === 'INSUFFICIENT_DATA' ? 'No data' : alarm.state}</Badge>
                            </li>
                        ))}
                    </ul>
                </Section>

                <Section title="Dead-letter queues" description="Work that failed every retry. One message is already a failure someone has to look at." status={report.deadLetters.status} detail={report.deadLetters.detail} absent="This environment has no dead-letter queues.">
                    <ul className="divide-y divide-border">
                        {(report.deadLetters.items ?? []).map((queue) => queue && (
                            <li key={queue.name} className="flex items-center justify-between gap-3 py-2">
                                <ConsoleLink href={queue.consoleUrl}>{queue.name}</ConsoleLink>
                                <Badge variant={queue.messages > 0 ? 'destructive' : 'success'}>{queue.messages}</Badge>
                            </li>
                        ))}
                    </ul>
                </Section>

                <Section title="Uptime check" description="The canary that loads the home page on a schedule." status={report.uptime.status} detail={report.uptime.detail} absent="No uptime check in this environment (uptimeCheckMinutes in config.ts).">
                    <div className="flex items-center justify-between gap-3">
                        <div>
                            {report.uptime.consoleUrl && <ConsoleLink href={report.uptime.consoleUrl}>Last run</ConsoleLink>}
                            <p className="text-xs text-muted-foreground">{report.uptime.lastRunAt ? new Date(report.uptime.lastRunAt).toLocaleString() : 'Has not run yet'}</p>
                        </div>
                        {report.uptime.lastRunState && <Badge variant={report.uptime.lastRunState === 'PASSED' ? 'success' : 'destructive'}>{report.uptime.lastRunState}</Badge>}
                    </div>
                </Section>

                <Section title="Spend this month" description="Counted over this project's tagged resources only." status={report.budget.status} detail={report.budget.detail} absent="No budget in this environment.">
                    <div className="flex items-baseline justify-between gap-3">
                        <p className="text-2xl font-semibold tabular-nums">${(report.budget.actualUsd ?? 0).toFixed(2)} <span className="text-sm font-normal text-muted-foreground">of ${(report.budget.limitUsd ?? 0).toFixed(0)}</span></p>
                        {report.budget.consoleUrl && <ConsoleLink href={report.budget.consoleUrl}>Budgets</ConsoleLink>}
                    </div>
                    {report.budget.forecastUsd != null && <p className="mt-1 text-xs text-muted-foreground">Forecast for the month: ${report.budget.forecastUsd.toFixed(2)}</p>}
                </Section>

                <Section title="Deploys" description="The latest builds of the site and the admin on this environment's branch." status={report.deploys.status} detail={report.deploys.detail} absent="Not a deployed environment — a sandbox has no hosting apps." wide>
                    <ul className="divide-y divide-border">
                        {(report.deploys.items ?? []).map((deploy) => deploy && (
                            <li key={deploy.consoleUrl} className="flex items-center justify-between gap-3 py-2 text-sm">
                                <div className="min-w-0">
                                    <ConsoleLink href={deploy.consoleUrl}>{deploy.app}{deploy.commitId ? ` · ${deploy.commitId.slice(0, 7)}` : ''}</ConsoleLink>
                                    <p className="text-xs text-muted-foreground">{deploy.startedAt ? new Date(deploy.startedAt).toLocaleString() : ''}</p>
                                </div>
                                <Badge variant={deploy.status === 'SUCCEED' ? 'success' : ['FAILED', 'CANCELLED'].includes(deploy.status) ? 'destructive' : 'secondary'}>{deploy.status}</Badge>
                            </li>
                        ))}
                    </ul>
                </Section>
            </div>
        </div>
    )
}

/** One area of the report, which says plainly when it is absent here or could not be read. */
function Section({ title, description, status, detail, absent, wide, children }: {
    title: string; description: string; status: string; detail?: string | null; absent: string; wide?: boolean; children: ReactNode
}) {
    const known = status as SectionStatus
    return (
        <Card className={wide ? 'lg:col-span-2' : undefined}>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                {known === 'ok' && children}
                {known === 'absent' && <p className="text-sm text-muted-foreground">{absent}</p>}
                {known === 'unknown' && <ErrorState title={`Could not read ${title.toLowerCase()}`} body={detail ?? undefined} />}
            </CardContent>
        </Card>
    )
}

/** A link out to the AWS console, in a new tab: the page stays where the incident is. */
function ConsoleLink({ href, children }: { href: string; children: ReactNode }) {
    return (
        <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium break-all underline-offset-4 hover:underline">
            {children}<ExternalLink className="size-3 shrink-0 text-muted-foreground" aria-hidden="true" />
        </a>
    )
}
